import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { isCommercialList } from '@epicuro-next/features/engagement/list-utils';
import { isNonNullish } from '@epicuro-next/utilities';
import { BehaviorSubject, merge, Subject, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
} from 'rxjs/operators';
import * as Sortable from 'sortablejs';

export type ProductReferences = ReadonlyArray<{ id: string }>;

const MEDIA_CLASS = 'media-wrapper';
const TARGET_MEDIA_RATIO = 0.75; // taken from current average p-card sizes
const RATIO_DIVISION_ERR_APPROXIMATE = 0.01;

@Component({
  selector: 'epc-fluid-dragable-list-content',
  templateUrl: './fluid-dragable-list-content.component.html',
  styleUrls: ['./fluid-dragable-list-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FluidDragableListContentComponent implements OnChanges, OnDestroy {
  @Input()
  public renderMedia = true;

  @Input()
  @HostBinding('style.--grid-item-min-width')
  public minGridItemSize = '294px';

  @Input()
  public startWithRatio = 0.51;

  @Input()
  public groupedByCategory = true;

  /**
   * Enables ngx-sortablejs directive root
   */
  @Input()
  public sortable = false;

  /**
   * Not used to render products.
   * It's used to track and reorganize dragable items.
   * The array order will be modified by ngx-sortable.
   * It's only required in dragable mode
   */
  @Input()
  public productReferences?: ProductReferences;

  @Input()
  public sortableJSHandleSelector?: string;

  @Output()
  public productsSorted = new EventEmitter<ProductReferences>();

  @Output()
  public productSorted = new EventEmitter<{
    oldIndex: number;
    newIndex: number;
  }>();

  public isCommercialList = isCommercialList;
  public sortableOptions?: Sortable.Options = {};
  public items?: FormArray;
  private itemsSub?: Subscription;

  public trueMediaAspectRatio$$ = new BehaviorSubject(this.startWithRatio);
  private definedAspectRatio$$ = new BehaviorSubject(this.startWithRatio);
  private destroy$$ = new Subject<void>();

  public mediaSizeClass$ = merge(
    this.trueMediaAspectRatio$$.pipe(debounceTime(200)),
    this.definedAspectRatio$$,
  ).pipe(
    distinctUntilChanged(),
    map((ratio) => ratio / TARGET_MEDIA_RATIO - RATIO_DIVISION_ERR_APPROXIMATE),
    map((ratio) => {
      if (ratio > 3) return 'quadruple-media';
      if (ratio > 2) return 'triple-media';
      if (ratio > 1) return 'double-media';
      if (ratio > 0.5) return '';
      return 'double-row-media';
    }),
  );

  public ngOnChanges({
    startWithRatio,
    items,
    sortableJSHandleSelector,
    productReferences,
  }: SimpleChanges) {
    if (
      startWithRatio &&
      startWithRatio.previousValue !== startWithRatio.currentValue
    ) {
      this.definedAspectRatio$$.next(startWithRatio.currentValue);
    }

    if (items || sortableJSHandleSelector) {
      this.sortableOptions = this.getSortableJSOptions();
    }

    if (productReferences) {
      this.createItems();
    }
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  private getSortableJSOptions() {
    return {
      handle: this.sortableJSHandleSelector,
      onMove: (event: Sortable.MoveEvent) => {
        return validateIfNotOnMediaElement(event.related);
      },
      onUpdate: (event: Sortable.SortableEvent) => {
        const { oldIndex, newIndex } = event;

        if (isNonNullish(oldIndex) && isNonNullish(newIndex)) {
          const mediaIndexSubstract = this.renderMedia ? 1 : 0;
          this.productSorted.emit({
            oldIndex: oldIndex - mediaIndexSubstract,
            newIndex: newIndex - mediaIndexSubstract,
          });
        }
      },
    } as Sortable.Options;
  }

  private createItems() {
    if (!this.productReferences || this.productReferences.length === 0) {
      this.items = undefined;
      this.itemsSub?.unsubscribe();
      return;
    }

    this.items = new FormArray(
      [
        this.renderMedia ? new FormControl() : undefined, // media
        ...this.productReferences.map((r) => new FormControl(r)),
      ].filter(isNonNullish),
    );

    this.itemsSub?.unsubscribe();
    this.itemsSub = this.items.valueChanges
      .pipe(takeUntil(this.destroy$$), debounceTime(300))
      .subscribe((values) => {
        this.productsSorted.emit(values);
      });
  }
}

function validateIfNotOnMediaElement(related: HTMLElement) {
  return !related.classList.contains(MEDIA_CLASS);
}
